import { useCallback, useEffect, useRef, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'

import clsx from 'clsx'

import { useInfiniteScroll } from 'shared/hooks/use-infinity-scroll'
import { useRequest } from 'shared/hooks/use-request'
import { getApiErrorMessage } from 'shared/lib/errorText'
import { AppIcon } from 'shared/ui/app-icon'
import { AllIcons } from 'shared/ui/app-icon/types'
import { SimpleLoader } from 'shared/ui/simple-loader'

import { MyObjectCard } from './ui/object-card'
import { MyObjectCardSkeleton } from './ui/object-card-skeleton'
import { MyObjectsStatusTabs } from './ui/status-tabs'

import {
  type MyObjectsPageResult,
  myObjectsPageFetchers,
} from './lib/fetch-my-objects-page'
import {
  DEFAULT_MY_OBJECT_STATUS,
  MY_OBJECTS_PAGE_SIZE,
} from './model/constants'
import {
  type MyObject,
  type MyObjectStatus,
  myObjectStatuses,
} from './model/types'
import s from './styles.module.scss'

interface MyObjectsListWidgetProps {
  onAddClick: () => void
  onActiveObjectClick: (objectId: string) => void
  onRejectedObjectClick: (draftId: string) => void
}

const isMyObjectStatus = (status: string | null): status is MyObjectStatus =>
  Boolean(status && myObjectStatuses.includes(status as MyObjectStatus))

const MY_OBJECTS_SKELETON_COUNT = 3

export const MyObjectsListWidget = ({
  onAddClick,
  onActiveObjectClick,
  onRejectedObjectClick,
}: MyObjectsListWidgetProps) => {
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()
  const statusParam = searchParams.get('status')
  const activeStatus = isMyObjectStatus(statusParam)
    ? statusParam
    : DEFAULT_MY_OBJECT_STATUS
  const [objects, setObjects] = useState<MyObject[]>([])
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [hasLoaded, setHasLoaded] = useState(false)
  const paginationTargetRef = useRef<HTMLDivElement>(null)
  const {
    error: requestError,
    isLoading,
    run,
  } = useRequest<MyObjectsPageResult>()
  const error = requestError === null ? null : getApiErrorMessage(requestError)

  const hasObjects = objects.length > 0
  const hasMore = page < totalPages
  const shouldShowInitialLoader = (!hasLoaded || isLoading) && !hasObjects
  const shouldShowEmptyState = hasLoaded && !isLoading && !hasObjects
  const shouldShowPaginationLoader = isLoading && hasObjects

  const fetchObjects = useCallback(
    async (status: MyObjectStatus, nextPage: number) => {
      const isFirstPage = nextPage === 1

      if (isFirstPage) {
        setObjects([])
        setPage(1)
        setTotalPages(1)
        setHasLoaded(false)
      }

      const result = await run((signal) =>
        myObjectsPageFetchers[status]({
          page: nextPage,
          pageSize: MY_OBJECTS_PAGE_SIZE,
          signal,
        }),
      )

      setHasLoaded(true)

      if (result === null) return

      setObjects((currentObjects) =>
        isFirstPage ? result.items : [...currentObjects, ...result.items],
      )
      setPage(result.page)
      setTotalPages(result.totalPages)
    },
    [run],
  )

  useEffect(() => {
    void fetchObjects(activeStatus, 1)
  }, [activeStatus, fetchObjects])

  useInfiniteScroll({
    targetRef: paginationTargetRef,
    canLoad: hasMore && !isLoading && !error,
    onLoad: () => fetchObjects(activeStatus, page + 1),
  })

  const handleStatusChange = (status: MyObjectStatus) => {
    if (status === activeStatus) {
      return
    }

    const nextParams = new URLSearchParams(searchParams)
    nextParams.set('status', status)
    setSearchParams(nextParams, { replace: true })
  }

  const getObjectClickHandler = (object: MyObject) => {
    if (object.status === 'active') {
      return () => onActiveObjectClick(object.id)
    }

    if (object.moderationStatus === 'rejected') {
      return () => onRejectedObjectClick(object.id)
    }

    return undefined
  }

  const content = shouldShowInitialLoader ? (
    <div className={s.list}>
      {Array.from({ length: MY_OBJECTS_SKELETON_COUNT }).map((_, index) => (
        <MyObjectCardSkeleton key={index} />
      ))}
    </div>
  ) : (
    <div className={s.list}>
      {objects.map((object) => (
        <MyObjectCard
          key={object.id}
          object={object}
          onClick={getObjectClickHandler(object)}
        />
      ))}

      {shouldShowEmptyState && (
        <p className={clsx('medium-14', s.empty)}>
          {t(`kyrgyzstan_block.my_objects.empty.${activeStatus}`)}
        </p>
      )}
    </div>
  )

  return (
    <>
      <div className={s.header}>
        <h1 className={clsx('bold-16', s.title)}>
          {t('kyrgyzstan_block.my_objects.section_title')}
        </h1>

        <button
          type="button"
          className={s.addButton}
          onClick={onAddClick}
        >
          <AppIcon
            id={AllIcons.PLUS}
            width={16}
            height={16}
            className={s.addIcon}
          />
          <span className={clsx('semi-bold-14', s.addText)}>
            {t('kyrgyzstan_block.my_objects.add')}
          </span>
        </button>
      </div>

      <MyObjectsStatusTabs
        activeStatus={activeStatus}
        onChange={handleStatusChange}
      />

      {content}

      {shouldShowPaginationLoader ? (
        <div className={s.paginationLoader}>
          <SimpleLoader />
        </div>
      ) : null}

      <div ref={paginationTargetRef} />
    </>
  )
}
